// ============================================================================
// ANALYTICS STATUS SETS
// Single source of truth for how work-item statuses are bucketed in
// capacity / productivity / costing / SLA / operations analytics.
//
// Rationale: analytics previously used scattered hardcoded lists like
// ['done', 'completed', 'closed'] which silently dropped real statuses seen in
// production (e.g. 'resolved', 'missed', 'ai_review', 'pending_invoice',
// 'apt_resolved'). Keep these sets aligned with operationalStatusConfig.ts and
// the item-type registry (types.yaml) when new statuses are introduced.
// ============================================================================

import { normalizeWorkflowStatusKey } from '@/lib/work-items/operationalStatusConfig';

/**
 * Successful terminal outcomes — the work was actually done and closed out.
 * Includes `apt_resolved` (job auto-derived from a resolved appointment) and
 * `done` (legacy task convention).
 */
export const ANALYTICS_COMPLETED_STATUSES = [
  'completed',
  'closed',
  'done',
  'resolved',
  'apt_resolved',
] as const;

/**
 * Scheduling failures: the appointment slot passed without the visit being
 * completed. `missed` = never attended; `no_show` = customer absent.
 * These MUST stay separate from cancellations — a missed appointment signals
 * under-achievement/over-planning while a cancellation removes planned work.
 */
export const ANALYTICS_MISSED_STATUSES = [
  'missed',
  'no_show',
] as const;

/**
 * Neutral terminal removals — work was intentionally taken off the plan.
 * `rescheduled` ends the visit slot; the follow-up appointment is a new item.
 */
export const ANALYTICS_CANCELLED_STATUSES = [
  'cancelled',
  'canceled',
  'void',
  'apt_cancelled',
  'rescheduled',
] as const;

/**
 * Attended but ended without a successful outcome.
 */
export const ANALYTICS_INCOMPLETE_STATUSES = [
  'unresolved',
  'partial',
  'partially_complete',
  'apt_partial',
  'failed',
  'skipped',
] as const;

/**
 * Operationally done, financially still open (billing pipeline).
 * Field capacity is freed, but the item is not fully closed. Analytics that
 * measure *work performed* should treat these as complete; workload/queue
 * views should exclude them from "active" lists.
 */
export const ANALYTICS_BILLING_PIPELINE_STATUSES = [
  'pending_invoice',
  'invoice_awaiting_approval',
  'billing_review',
  'converted',
] as const;

/**
 * Every status after which the item no longer represents open field work.
 * Use for "active work" / "due soon" / "overdue" exclusions.
 */
export const ANALYTICS_TERMINAL_STATUSES = [
  ...ANALYTICS_COMPLETED_STATUSES,
  ...ANALYTICS_MISSED_STATUSES,
  ...ANALYTICS_CANCELLED_STATUSES,
  ...ANALYTICS_INCOMPLETE_STATUSES,
  ...ANALYTICS_BILLING_PIPELINE_STATUSES,
] as const;

/**
 * Statuses that count as "work finished" for completion-style metrics
 * (items completed in a period, completion timestamps). Includes the billing
 * pipeline because the operational work is done.
 */
export const ANALYTICS_WORK_DONE_STATUSES = [
  ...ANALYTICS_COMPLETED_STATUSES,
  ...ANALYTICS_BILLING_PIPELINE_STATUSES,
] as const;

export type SchedulingOutcome =
  | 'completed'      // successful terminal
  | 'missed'         // scheduling failure (missed / no_show)
  | 'cancelled'      // intentionally removed / rescheduled
  | 'incomplete'     // attended but ended without resolution
  | 'billing'        // operationally done, in billing pipeline
  | 'active'         // work in flight (in_progress, en_route, arrived, ai_review, ...)
  | 'open';          // not started (open, scheduled, backlog, new, draft, ...)

const COMPLETED_SET = new Set<string>(ANALYTICS_COMPLETED_STATUSES);
const MISSED_SET = new Set<string>(ANALYTICS_MISSED_STATUSES);
const CANCELLED_SET = new Set<string>(ANALYTICS_CANCELLED_STATUSES);
const INCOMPLETE_SET = new Set<string>(ANALYTICS_INCOMPLETE_STATUSES);
const BILLING_SET = new Set<string>(ANALYTICS_BILLING_PIPELINE_STATUSES);
const TERMINAL_SET = new Set<string>(ANALYTICS_TERMINAL_STATUSES);
const WORK_DONE_SET = new Set<string>(ANALYTICS_WORK_DONE_STATUSES);

const ACTIVE_STATUSES = new Set<string>([
  'in_progress',
  'working',
  'checked_in',
  'paused',
  'travel_paused',
  'en_route',
  'on_route',
  'traveling',
  'dispatched',
  'arrived',
  'on_site',
  'on_hold',
  'pending_approval',
  'pending_closure',
  'admin_reopened',
  'ai_review',
  'ai_on_hold',
  'running_late',
  'behind_schedule',
  'over_schedule',
  'assigned',
]);

export function isAnalyticsCompletedStatus(status?: string | null): boolean {
  return COMPLETED_SET.has(normalizeWorkflowStatusKey(status));
}

export function isMissedLikeStatus(status?: string | null): boolean {
  return MISSED_SET.has(normalizeWorkflowStatusKey(status));
}

export function isAnalyticsCancelledStatus(status?: string | null): boolean {
  return CANCELLED_SET.has(normalizeWorkflowStatusKey(status));
}

export function isAnalyticsIncompleteStatus(status?: string | null): boolean {
  return INCOMPLETE_SET.has(normalizeWorkflowStatusKey(status));
}

export function isBillingPipelineStatus(status?: string | null): boolean {
  return BILLING_SET.has(normalizeWorkflowStatusKey(status));
}

/**
 * True when the item no longer represents open field work for analytics
 * purposes (any terminal or billing-pipeline outcome).
 */
export function isAnalyticsTerminalStatus(status?: string | null): boolean {
  return TERMINAL_SET.has(normalizeWorkflowStatusKey(status));
}

/**
 * True when the operational work is finished (success OR billing pipeline).
 */
export function isAnalyticsWorkDoneStatus(status?: string | null): boolean {
  return WORK_DONE_SET.has(normalizeWorkflowStatusKey(status));
}

export function isAnalyticsActiveStatus(status?: string | null): boolean {
  return ACTIVE_STATUSES.has(normalizeWorkflowStatusKey(status));
}

export function deriveSchedulingOutcome(status?: string | null): SchedulingOutcome {
  const normalized = normalizeWorkflowStatusKey(status);
  if (COMPLETED_SET.has(normalized)) return 'completed';
  if (MISSED_SET.has(normalized)) return 'missed';
  if (CANCELLED_SET.has(normalized)) return 'cancelled';
  if (INCOMPLETE_SET.has(normalized)) return 'incomplete';
  if (BILLING_SET.has(normalized)) return 'billing';
  if (ACTIVE_STATUSES.has(normalized)) return 'active';
  return 'open';
}

/**
 * Human-friendly label for a raw status key ('ai_review' → 'Ai Review').
 */
export function humanizeStatusKey(status?: string | null): string {
  const normalized = normalizeWorkflowStatusKey(status);
  return normalized
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Unknown';
}

/**
 * Fields to persist alongside a work-items.status transition so completion /
 * response timestamps exist for analytics. Columns are only ever set once
 * (first transition wins) — reopening an item does not rewrite history.
 */
export function getStatusTransitionTimestampFields(params: {
  previousStatus?: string | null;
  nextStatus?: string | null;
  item: {
    completed_at?: string | Date | null;
    resolved_at?: string | Date | null;
    closed_at?: string | Date | null;
    first_responded_at?: string | Date | null;
  };
  now?: Date;
}): Record<string, Date> {
  const now = params.now ?? new Date();
  const next = normalizeWorkflowStatusKey(params.nextStatus);
  const previous = normalizeWorkflowStatusKey(params.previousStatus);
  const fields: Record<string, Date> = {};

  if (!next || next === previous) {
    return fields;
  }

  // First field response: travel / arrival / on-site / work started.
  const RESPONSE_STATUSES = new Set([
    'en_route', 'on_route', 'traveling', 'dispatched',
    'arrived', 'on_site', 'in_progress', 'working', 'checked_in',
  ]);
  if (!params.item.first_responded_at && RESPONSE_STATUSES.has(next)) {
    fields.first_responded_at = now;
  }

  // Successful completion / operationally done.
  if (WORK_DONE_SET.has(next)) {
    if (!params.item.completed_at) fields.completed_at = now;
    if ((next === 'resolved' || next === 'apt_resolved') && !params.item.resolved_at) {
      fields.resolved_at = now;
    }
    if ((next === 'closed' || next === 'converted') && !params.item.closed_at) {
      fields.closed_at = now;
    }
  }

  return fields;
}
